import { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from 'src/components/button';
import { Section } from 'src/components/section';
import { ControlledField, FieldTypes } from 'src/components/controlled-field';
import { getTheme } from 'src/utils/getTheme';
import { Building } from 'src/shared/types/building';

import { AddressDetails } from './address-details';
import { styles } from './styles';
import { UserDetails } from './user-details';
import { SpanishTaxDetails } from './spanish-tax-details';
import { userDataKeys } from './constants';

import type { DetailsFormProps, UserDataFormValues } from './types';

interface ExtendedDetailsFormProps extends DetailsFormProps<UserDataFormValues> {
    loading: boolean;
    userHasBookings: boolean;
    userLoggedIn: boolean;
    building: Building;
}

export const DetailsForm: FC<ExtendedDetailsFormProps> = ({
    control,
    onFormSubmit,
    hasRoommates,
    loading,
    userHasBookings,
    userLoggedIn,
    building,
}) => {
    const { t } = useTranslation();
    const {
        formState: { isValid },
        handleSubmit,
    } = useFormContext<UserDataFormValues>();

    const { brand } = getTheme();

    const getCtaName = () => {
        const loggedIn = userLoggedIn ? 'logged_in_' : '';
        const roommates = hasRoommates ? 'roommates' : 'agreements';

        return `your_details_form_cta_${loggedIn}${roommates}`;
    };

    const onSubmit = handleSubmit(() => {
        if (isValid) {
            onFormSubmit();
        }
    });

    return (
        <Section header={t('your_details_form_title')} style={styles.container}>
            <UserDetails control={control} />
            <AddressDetails control={control} />
            {building.spanish_tax_applicable && (
                <SpanishTaxDetails control={control} userHasBookings={userHasBookings} />
            )}

            <ControlledField
                control={control}
                name={userDataKeys.userFormConfirmation}
                label={t('your_details_form_confirmation_label', {
                    brand,
                })}
                fieldType={FieldTypes.Checkbox}
                required
                id="user-details-student-confirmation"
            />

            <Button
                variant="contained"
                onClick={onSubmit}
                css={styles.cta}
                loading={loading}
                id="user-details-submit"
            >
                {t(getCtaName())}
            </Button>
        </Section>
    );
};
